import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Download, AlertCircle, ZoomIn, ZoomOut, Menu } from 'lucide-react';
import classNames from 'classnames';
import Button from '@/components/Button';
import LoadingSpin from '@/components/Button/LoadingSpin';
import type { ResourceAssetItem, ResourceHistoryItem } from '../../../shared/types';
import { usePreviewModal } from '../../hooks/usePreviewModal';

interface PreviewModalResource {
  documentId: string;
  title: string;
  type?: {
    type: string;
  } | null;
  category?: {
    category: string;
  } | null;
  resourceHistory?: ResourceHistoryItem[] | null;
}

interface PreviewModalProps {
  resource: PreviewModalResource | null;
  asset: ResourceAssetItem | null;
  assets?: ResourceAssetItem[] | null;
  currentAssetIndex?: number;
  onAssetChange?: (index: number) => void;
  isOpen: boolean;
  onClose: () => void;
  onPreviewStatusChange?: (documentId: string, previewStatus: 'unavailable') => void;
  downloadButtonText?: string;
}

export const PreviewModal = ({
  resource,
  asset,
  assets,
  currentAssetIndex = 0,
  onAssetChange,
  isOpen,
  onClose,
  onPreviewStatusChange,
  downloadButtonText = 'Download',
}: PreviewModalProps) => {
  const {
    MIN_ZOOM,
    MAX_ZOOM,
    isLoading,
    hasError,
    objectUrl,
    isDownloading,
    zoom,
    containerRef,
    isDragging,
    position,
    showSidebar,
    isSidebarOpen,
    handleDownload,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleWheel,
    handleZoomOut,
    handleZoomReset,
    handleZoomIn,
    handleClose,
    handleAssetClick,
    handleDocumentClick,
    toggleSidebar,
  } = usePreviewModal({
    resource,
    asset,
    assets,
    isOpen,
    onAssetChange,
    onClose,
    onPreviewStatusChange,
  });

  const renderPreviewContent = () => {
    if (isLoading) {
      return (
        <div className="flex h-full w-[800px] animate-pulse items-center justify-center bg-gray-200">
          <LoadingSpin className="!h-12 !w-12 text-primary-600" />
        </div>
      );
    }

    if (hasError || !objectUrl) {
      return (
        <div className="flex h-96 flex-col items-center justify-center gap-4 px-4">
          <AlertCircle size={48} className="text-red-500" />
          <p className="text-center font-haffer text-sh-16-24 font-semibold text-gray-900">Failed to load preview</p>
        </div>
      );
    }

    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div
        ref={containerRef}
        className={classNames(
          'relative flex h-full w-full items-center justify-center overflow-hidden bg-gray-50',
          !isDragging && 'cursor-grab',
          isDragging && 'cursor-grabbing',
        )}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleDocumentClick}>
        <img
          src={objectUrl}
          alt="Preview"
          draggable={false}
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
          }}
          className={classNames(
            'max-w-none origin-center select-none',
            !isDragging && 'transition-all duration-300 ease-out',
          )}
        />
      </div>
    );
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <Dialog.Panel className="relative flex h-[85vh] max-w-5xl transform flex-col overflow-hidden rounded-lg bg-white shadow-xl transition-all">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-2 sm:px-6">
                  <div className="flex min-w-0 flex-1 items-center gap-4">
                    {showSidebar && !isSidebarOpen && (
                      <button
                        type="button"
                        onClick={toggleSidebar}
                        className="shrink-0 rounded-md p-1.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                        title="Show assets">
                        <Menu size={20} />
                      </button>
                    )}
                    <h2 className="truncate font-haffer text-sh-18-24 font-bold text-gray-900">
                      {asset?.title || 'Preview'}
                    </h2>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="hidden items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1 sm:flex">
                      <button
                        type="button"
                        onClick={handleZoomOut}
                        disabled={zoom <= MIN_ZOOM}
                        className="rounded p-1.5 text-gray-600 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-40"
                        title="Zoom out">
                        <ZoomOut size={18} />
                      </button>
                      <button
                        type="button"
                        onClick={handleZoomReset}
                        className="min-w-[52px] rounded px-2 py-1 text-center font-haffer text-sh-12-16 font-medium text-gray-700 transition-colors hover:bg-gray-200"
                        title="Reset zoom">
                        {Math.round(zoom * 100)}%
                      </button>
                      <button
                        type="button"
                        onClick={handleZoomIn}
                        disabled={zoom >= MAX_ZOOM}
                        className="rounded p-1.5 text-gray-600 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-40"
                        title="Zoom in">
                        <ZoomIn size={18} />
                      </button>
                    </div>
                    <Button theme="outline" onClick={handleDownload} disabled={isDownloading} className="shrink-0">
                      {downloadButtonText}
                      <Download size={24} color="#E3786D" />
                    </Button>
                    <button
                      type="button"
                      className="shrink-0 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
                      onClick={handleClose}>
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                <div className="relative flex flex-1 overflow-hidden">
                  {showSidebar && assets && isSidebarOpen && (
                    <div className="w-64 border-r border-gray-200 bg-gray-50">
                      <div className="p-4">
                        <h3 className="mb-3 font-haffer text-sh-14-20 font-semibold text-gray-700">Assets</h3>
                        <div className="max-h-[calc(85vh-120px)] space-y-1 overflow-y-auto">
                          {assets.map((item, index) => {
                            const isActive = index === currentAssetIndex;
                            return (
                              <button
                                key={item.documentId}
                                type="button"
                                onClick={() => handleAssetClick(index)}
                                className={classNames(
                                  'w-full rounded-md px-3 py-2 text-left transition-colors',
                                  'font-haffer text-sh-14-20',
                                  isActive ? 'bg-primary-600 text-white' : 'text-gray-700 hover:bg-gray-200',
                                )}>
                                <div className="truncate">{item.title}</div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="relative flex-1 overflow-hidden">{renderPreviewContent()}</div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
